import axios from 'axios';

const API_URL = 'https://serv.mesds.ru/api';

const api = axios.create({
    baseURL: API_URL,
    timeout: 30000, // ✅ 30 секунд таймаут
   
});

let accessToken = localStorage.getItem('access_token');
let onTokenUpdate = null;
let isRefreshing = false;
let refreshSubscribers = [];

// ✅ Функция для проверки ошибки сети
const isNetworkError = (error) => {
    return !error.response || 
           error.code === 'ECONNABORTED' || 
           error.code === 'ECONNRESET' ||
           error.message === 'Network Error' ||
           error.message === 'Failed to fetch';
};

export const setTokenUpdateCallback = (callback) => {
    onTokenUpdate = callback;
};

export const setAccessToken = (token) => {
    accessToken = token;
    if (token) {
        localStorage.setItem('access_token', token);
    } else {
        localStorage.removeItem('access_token');
    }
    if (onTokenUpdate) {
        onTokenUpdate(token);
    }
    
};

export const setTokens = (access_token, refresh_token) => {
    accessToken = access_token;
    localStorage.setItem('access_token', access_token);
    
    if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
    }
    
    if (onTokenUpdate) {
        onTokenUpdate(access_token);
    }
    
    window.dispatchEvent(new CustomEvent('tokenUpdated', { 
        detail: { token: access_token } 
    }));
};

export const clearTokens = () => {
    accessToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    if (onTokenUpdate) {
        onTokenUpdate(null);
    }
};

// Request interceptor
api.interceptors.request.use(
    (config) => {
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // ✅ 1. Сетевая ошибка
        if (isNetworkError(error)) {
            console.warn('🌐 Network error, keeping tokens:', error.message);
            window.dispatchEvent(new CustomEvent('networkError', { 
                detail: { error: error.message } 
            }));
            return Promise.reject(error);
        }
        
        // ✅ 2. Ошибки сервера (503, 502, 504)
        if ([502, 503, 504].includes(error.response?.status)) {
            console.warn('🔧 Server unavailable, keeping tokens');
            window.dispatchEvent(new CustomEvent('serverError', { 
                detail: { status: error.response?.status } 
            }));
            return Promise.reject(error);
        }
        
        // ✅ 3. Запросы на /auth/refresh
        if (originalRequest.url?.includes('/auth/refresh')) {
            // Любая ошибка при рефреше — удаляем токены
            clearTokens();
            window.location.href = '/login';
            return Promise.reject(error);
        }
        
        // ✅ 4. Другие /auth/ запросы (логин, регистрация)
        if (originalRequest.url?.includes('/auth/')) {
            return Promise.reject(error);
        }
        
        // ✅ 5. Обработка 401
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshSubscribers.push({ resolve, reject });
                })
                .then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                })
                .catch(err => Promise.reject(err));
            }
            
            originalRequest._retry = true;
            isRefreshing = true;
            
            try {
                const refreshToken = localStorage.getItem('refresh_token');
                if (!refreshToken) {
                    throw new Error('No refresh token');
                }
                
                const response = await axios.post(`${API_URL}/auth/refresh`, {
                    refresh_token: refreshToken
                });
                
                const { access_token, refresh_token } = response.data;
                
                // ✅ Только setTokens, без дублирования!
                setTokens(access_token, refresh_token);
                
                originalRequest.headers.Authorization = `Bearer ${access_token}`;
                refreshSubscribers.forEach(sub => sub.resolve(access_token));
                refreshSubscribers = [];
                
                return api(originalRequest);
                
            } catch (refreshError) {
                // ✅ Если ошибка сети при рефреше — не удаляем токены
                if (isNetworkError(refreshError)) {
                    console.warn('🌐 Network error during refresh, keeping tokens');
                    window.dispatchEvent(new CustomEvent('networkError', { 
                        detail: { error: refreshError.message } 
                    }));
                    refreshSubscribers.forEach(sub => sub.reject(refreshError));
                    refreshSubscribers = [];
                    return Promise.reject(refreshError);
                }
                
                // ❌ Только если рефреш вернул ошибку — удаляем токены
                clearTokens();
                window.location.href = '/login';
                refreshSubscribers.forEach(sub => sub.reject(refreshError));
                refreshSubscribers = [];
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
        
        return Promise.reject(error);
    }
);

export default api;